/**
 * @fileoverview Host Output Sync Hook
 *
 * Bu hook, host output (ChatGPT'den gelen) ile local output state'ini
 * senkronize eder. 8-dependency useEffect problemini cozer.
 *
 * @module hooks/useHostOutputSync
 */

import { useEffect, useRef } from "react";
import { useOutputIdentity } from "./useOutputIdentity.js";

/**
 * Iki output identity nesnesini karsilastirir.
 *
 * @private
 * @param {Object} a - Ilk identity
 * @param {Object} b - Ikinci identity
 * @returns {boolean} Esitse true
 */
function shallowEqualIdentity(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.kind === b.kind &&
    a.total === b.total &&
    a.page === b.page &&
    a.path === b.path &&
    a.filters === b.filters &&
    a.listingsId === b.listingsId &&
    a.projectsId === b.projectsId
  );
}

/**
 * Host output ile local output'u senkronize eder.
 *
 * Bu hook, hostOutput'un icerigi degistiginde setOutput'u cagirir.
 * Referans degisikliklerini degil, gercek icerik degisikliklerini izler.
 *
 * **Cozulen Problem:**
 * App.jsx'teki 8-dependency useEffect'i tek bir hook'a tasir:
 * ```javascript
 * // ONCEKI (App.jsx'te)
 * useEffect(() => {
 *   setOutput(hostOutput);
 * }, [
 *   hostOutput?.kind,
 *   hostOutput?.total,
 *   hostOutput?.page,
 *   hostOutput?.query?.path,
 *   hostOutput?.query?.filters,
 *   hostListingsIdentity,
 *   hostProjectsIdentity,
 *   setOutput,
 * ]);
 * ```
 *
 * @function useHostOutputSync
 * @param {Object|null} hostOutput - ChatGPT'den gelen tool output
 * @param {Function} setOutput - Local output state setter
 *
 * @example
 * // App.jsx'te kullanim
 * useHostOutputSync(hostOutput, setOutput);
 */
export function useHostOutputSync(hostOutput, setOutput) {
  const identity = useOutputIdentity(hostOutput);
  const prevIdentityRef = useRef(identity);

  useEffect(() => {
    // Sadece icerik gercekten degistiyse guncelle
    if (!shallowEqualIdentity(identity, prevIdentityRef.current)) {
      setOutput(hostOutput);
      prevIdentityRef.current = identity;
    }
  }, [identity, hostOutput, setOutput]);
}

export default useHostOutputSync;
